import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, ArrowUpRight, Check, X } from "lucide-react";
import { STEPS } from "@/data";

const ease = [0.16, 1, 0.3, 1];

export function Hotspot({ x, y, label, onClick, testid, icon }) {
    return (
        <button
            type="button"
            data-interactive
            data-testid={testid}
            onClick={onClick}
            aria-label={label}
            style={{ left: `${x}%`, top: `${y}%` }}
            className="group/hs absolute z-10 -translate-x-1/2 -translate-y-1/2"
        >
            <span className="hs-halo absolute inset-0 rounded-full border border-white/60" />
            <span className="relative flex h-9 w-9 items-center justify-center rounded-full border border-white/90 bg-white/15 backdrop-blur-[2px] transition-[transform,background-color] duration-300 group-hover/hs:scale-110 group-hover/hs:bg-white/90 group-hover/hs:text-[#121211]">
                {icon}
            </span>
            {label && (
                <span className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 translate-y-1 whitespace-nowrap bg-[#FBF9F5] px-3 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.22em] text-[#121211] opacity-0 transition-[opacity,transform] duration-300 group-hover/hs:translate-y-0 group-hover/hs:opacity-100">
                    {label}
                </span>
            )}
        </button>
    );
}

export function MoveDemo({ image }) {
    const [moved, setMoved] = useState(false);
    return (
        <div className="relative h-full w-full overflow-hidden" data-testid="demo-move">
            <motion.img
                src={image}
                alt="Move"
                animate={{ scale: moved ? 1.55 : 1, x: moved ? "-6%" : "0%", y: moved ? "6%" : "0%" }}
                transition={{ duration: 1.6, ease }}
                className="h-full w-full object-cover"
                style={{ transformOrigin: "50% 45%" }}
            />
            <Hotspot
                x={moved ? 62 : 50}
                y={moved ? 56 : 66}
                label={moved ? "Back to the hall" : "Move forward"}
                testid="demo-move-arrow"
                onClick={() => setMoved((v) => !v)}
                icon={
                    <ArrowUp
                        className={`h-4 w-4 text-white transition-[transform,color] duration-500 group-hover/hs:text-[#121211] ${moved ? "rotate-180" : ""}`}
                    />
                }
            />
        </div>
    );
}

export function LookDemo({ image }) {
    const [pos, setPos] = useState(50);
    const [dragging, setDragging] = useState(false);
    const start = useRef(null);
    return (
        <div
            className={`relative h-full w-full touch-pan-y overflow-hidden ${dragging ? "cursor-grabbing" : "cursor-grab"}`}
            data-testid="demo-look"
            data-interactive
            onPointerDown={(e) => {
                start.current = { x: e.clientX, pos };
                setDragging(true);
                e.currentTarget.setPointerCapture(e.pointerId);
            }}
            onPointerMove={(e) => {
                if (!start.current) return;
                const w = e.currentTarget.clientWidth;
                const next = start.current.pos - ((e.clientX - start.current.x) / w) * 120;
                setPos(Math.min(100, Math.max(0, next)));
            }}
            onPointerUp={() => {
                start.current = null;
                setDragging(false);
            }}
            onPointerCancel={() => {
                start.current = null;
                setDragging(false);
            }}
        >
            <img
                src={image}
                alt="Look"
                draggable={false}
                className={`h-full w-[180%] max-w-none object-cover ${dragging ? "" : "transition-transform duration-300"}`}
                style={{ transform: `translateX(-${pos * 0.4444}%)` }}
                data-testid="demo-look-image"
            />
            <div className="pointer-events-none absolute inset-x-6 bottom-5 flex items-center gap-3">
                <span className="h-px flex-1 bg-white/30">
                    <span
                        className="block h-px w-6 bg-white transition-transform duration-150"
                        style={{ transform: `translateX(${pos}%)` }}
                    />
                </span>
                <span className="font-mono2 text-[9px] uppercase tracking-[0.28em] text-white/80">
                    Drag to look · 360°
                </span>
            </div>
        </div>
    );
}

export function DiscoverDemo({ image }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative h-full w-full overflow-hidden" data-testid="demo-discover">
            <motion.img
                src={image}
                alt="Discover"
                animate={{ scale: open ? 1.06 : 1 }}
                transition={{ duration: 1.2, ease }}
                className="h-full w-full object-cover"
            />
            {!open && (
                <Hotspot
                    x={35}
                    y={62}
                    label="Chef's menu"
                    testid="demo-discover-hotspot"
                    onClick={() => setOpen(true)}
                    icon={<span className="block h-2 w-2 rounded-full bg-white transition-colors duration-300 group-hover/hs:bg-[#121211]" />}
                />
            )}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ y: "100%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "100%", opacity: 0 }}
                        transition={{ duration: 0.7, ease }}
                        className="absolute inset-x-4 bottom-4 bg-[#FBF9F5] p-4 text-[#121211] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)]"
                        data-testid="demo-discover-card"
                    >
                        <div className="flex gap-4">
                            <img
                                src={image}
                                alt=""
                                className="h-20 w-16 shrink-0 object-cover"
                                data-testid="demo-discover-thumb"
                            />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-3">
                                    <p className="font-mono2 text-[9px] uppercase tracking-[0.3em] text-[#6B6862]">
                                        Hotspot · Dining
                                    </p>
                                    <button
                                        data-interactive
                                        data-testid="demo-discover-close"
                                        onClick={() => setOpen(false)}
                                        aria-label="Close"
                                        className="-mr-1 -mt-1 rounded-full p-1 transition-colors duration-300 hover:bg-[#121211]/10"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                                <h4 className="mt-1.5 font-display text-2xl font-light leading-tight">
                                    Chef’s Tasting Menu
                                </h4>
                                <p className="mt-1.5 text-xs font-light leading-relaxed text-[#121211]/70">
                                    Seven Courses of Coastal Californian Cooking
                                </p>
                            </div>
                        </div>
                        <button
                            data-interactive
                            data-testid="demo-discover-cta"
                            onClick={() => setOpen(false)}
                            className="group/cta mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#121211] py-2.5 font-mono2 text-[10px] uppercase tracking-[0.24em] text-[#FBF9F5] transition-colors duration-300 hover:bg-[#2C2B28]"
                        >
                            View Full Menu
                            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/cta:-translate-y-0.5 group-hover/cta:translate-x-0.5" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

const DAYS = ["Thu 12", "Fri 13", "Sat 14"];
const TIMES = ["10:00", "13:30", "16:00"];

export function ActDemo({ image }) {
    const [open, setOpen] = useState(false);
    const [day, setDay] = useState(null);
    const [time, setTime] = useState(null);
    const [done, setDone] = useState(false);
    const reset = () => {
        setOpen(false);
        setDay(null);
        setTime(null);
        setDone(false);
    };
    return (
        <div className="relative h-full w-full overflow-hidden" data-testid="demo-act">
            <motion.img
                src={image}
                alt="Act"
                animate={{ scale: open ? 1.06 : 1 }}
                transition={{ duration: 1.2, ease }}
                className="h-full w-full object-cover"
            />
            {!open && (
                <button
                    data-interactive
                    data-testid="demo-act-hotspot"
                    onClick={() => setOpen(true)}
                    className="group/act absolute left-1/2 top-[58%] z-10 flex -translate-x-1/2 -translate-y-1/2 items-center gap-3 rounded-full border border-white/80 bg-white/15 py-2 pl-2 pr-5 font-mono2 text-[9px] uppercase tracking-[0.24em] text-white backdrop-blur-[2px] transition-[background-color,color,transform] duration-300 hover:scale-[1.04] hover:bg-white hover:text-[#121211]"
                >
                    <span className="relative flex h-6 w-6 items-center justify-center rounded-full border border-white/80 transition-colors duration-300 group-hover/act:border-[#121211]/40">
                        <span className="hs-halo absolute inset-0 rounded-full border border-white/60" />
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    </span>
                    Book an appointment
                </button>
            )}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ y: "100%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "100%", opacity: 0 }}
                        transition={{ duration: 0.7, ease }}
                        className="absolute inset-x-4 bottom-4 bg-[#FBF9F5] p-5 text-[#121211]"
                        data-testid="demo-act-card"
                    >
                        <div className="flex items-start justify-between gap-4">
                            <p className="font-mono2 text-[9px] uppercase tracking-[0.3em] text-[#6B6862]">
                                Book an appointment
                            </p>
                            <button
                                data-interactive
                                data-testid="demo-act-close"
                                onClick={reset}
                                aria-label="Close"
                                className="-mr-1 -mt-1 rounded-full p-1 transition-colors duration-300 hover:bg-[#121211]/10"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        {done ? (
                            <div className="mt-3" data-testid="demo-act-confirmed">
                                <p className="flex items-center gap-2 font-display text-2xl font-light leading-tight">
                                    <Check className="h-5 w-5" /> Requested
                                </p>
                                <p className="mt-2 text-xs font-light leading-relaxed text-[#121211]/70">
                                    {day} · {time}. The spa will confirm within the hour.
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="mt-3 flex gap-2">
                                    {DAYS.map((d) => (
                                        <button
                                            key={d}
                                            data-interactive
                                            data-testid={`demo-act-day-${d.split(" ")[1]}`}
                                            onClick={() => setDay(d)}
                                            className={`whitespace-nowrap rounded-full border px-3 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.16em] transition-colors duration-300 ${day === d ? "border-[#121211] bg-[#121211] text-[#FBF9F5]" : "border-[#121211]/25 hover:border-[#121211]"}`}
                                        >
                                            {d}
                                        </button>
                                    ))}
                                </div>
                                <div className="mt-2 flex gap-2">
                                    {TIMES.map((t) => (
                                        <button
                                            key={t}
                                            data-interactive
                                            data-testid={`demo-act-time-${t.replace(":", "")}`}
                                            onClick={() => setTime(t)}
                                            className={`rounded-full border px-3 py-1.5 font-mono2 text-[9px] tracking-[0.2em] transition-colors duration-300 ${time === t ? "border-[#121211] bg-[#121211] text-[#FBF9F5]" : "border-[#121211]/25 hover:border-[#121211]"}`}
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    data-interactive
                                    data-testid="demo-act-confirm"
                                    disabled={!day || !time}
                                    onClick={() => setDone(true)}
                                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#121211] py-2.5 font-mono2 text-[10px] uppercase tracking-[0.24em] text-[#FBF9F5] transition-[opacity,background-color] duration-300 hover:bg-[#2C2B28] disabled:opacity-30"
                                >
                                    Confirm request <ArrowUpRight className="h-3.5 w-3.5" />
                                </button>
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export const DEMOS = {
    "01": MoveDemo,
    "02": LookDemo,
    "03": DiscoverDemo,
    "04": ActDemo,
};

export function stepDemo(step) {
    const Demo = DEMOS[step.index];
    return <Demo image={step.image} />;
}

export { STEPS };
